import { constructEarthRangerUrl } from './earthRangerUrl';

type Translate = (key: any, options?: any) => string;

// The one rule for an integration's address, shared by every screen that asks
// for one (the creation wizard, the destination modal, the destination page and
// the flow map's General pane). They had four copies that drifted: one was left
// untranslated and spelled the message differently, so the same mistake produced
// different text depending on where you made it.
//
// EarthRanger accounts are addressed by site NAME (<site>.pamdas.org) rather than
// a full URL, so `asSiteName` switches both the validation and the wording.
export function validateBaseUrl({
  value,
  asSiteName,
  optional,
  t,
}: {
  value: string;
  asSiteName?: boolean;
  // The technology derives its address from its configuration, so an empty
  // value is an answer rather than an omission.
  optional?: boolean;
  t: Translate;
}): string | undefined {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    if (optional) return undefined;
    return asSiteName ? t('Site name is required.') : t('URL is required.');
  }
  if (asSiteName) {
    if (!/^[a-zA-Z0-9.-]+$/.test(trimmed)) return t('Enter a valid site name.');
    try {
      new URL(constructEarthRangerUrl(trimmed));
      return undefined;
    } catch {
      return t('Enter a valid site name.');
    }
  }
  // `new URL` accepts any scheme, so "smart.example.org:443/server" parses
  // with "smart.example.org:" as its scheme. Routing only reaches http(s)
  // addresses with a host, so anything else is refused here instead.
  try {
    const url = new URL(trimmed);
    if (['http:', 'https:'].includes(url.protocol) && url.hostname)
      return undefined;
  } catch {
    // Falls through to the same message as an address that isn't http(s).
  }
  return t('Enter a valid URL including the scheme.');
}
